/**
 * lib/ninetailed/personalization-storage.ts
 *
 * The browser-storage footprint of the Ninetailed SDK, plus a purge helper used
 * when personalization consent is REVOKED.
 *
 * The SDK persists everything in `localStorage`:
 *  - `__nt_anonymous_id__` — the pseudonymous visitor id used to fetch a profile.
 *  - `__nt_profile__` / `__nt_experiences__` / `__nt_changes__` — fallback caches
 *    of the last resolved profile/experiences (seeded back into `<Experience>` if
 *    a later flush errors).
 *  - `__nt-consent__` — the privacy plugin's own consent flag (`'accepted'`), read
 *    at SDK init to decide whether to start blocked or unblocked.
 *
 * On revoke we hard-purge ALL of these (see `NinetailedExperienceProvider`). The
 * consent flag is included deliberately: the provider is remounted on the consent
 * flip so a fresh SDK is built, and that SDK reads `__nt-consent__` during init —
 * if the stale `'accepted'` value were left behind, the fresh SDK would start
 * UNBLOCKED and immediately re-personalize, defeating the revoke. Purging the flag
 * here (synchronously, before the remount) guarantees the fresh SDK starts in the
 * strict blocked state and renders the baseline.
 *
 * Keep this list in sync with the SDK constants in
 * `@ninetailed/experience.js` (ANONYMOUS_ID / PROFILE_FALLBACK_CACHE /
 * EXPERIENCES_FALLBACK_CACHE / CHANGES_FALLBACK_CACHE / CONSENT).
 */

export const NINETAILED_STORAGE_KEYS = [
  '__nt_anonymous_id__',
  '__nt_profile__',
  '__nt_experiences__',
  '__nt_changes__',
  '__nt-consent__',
] as const;

/**
 * Remove every Ninetailed localStorage key. Safe to call on the server (no-op)
 * and when `localStorage` is unavailable (private mode); idempotent, so calling
 * it repeatedly — including React StrictMode's double render — is harmless.
 */
export function purgePersonalizationStorage(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    for (const key of NINETAILED_STORAGE_KEYS) {
      window.localStorage.removeItem(key);
    }
  } catch {
    // localStorage can throw (disabled cookies / private mode). Nothing to purge.
  }
}
